import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    defaultColor: { type: String, default: "#4ade80" }, // Default color as fallback
  };

  connect() {
    // Store click coordinates when clicking anywhere in the element
    this.lastClickX = null;
    this.lastClickY = null;

    this.element.addEventListener(
      "click",
      this.storeClickCoordinates.bind(this),
    );

    // Get the accent color from CSS variables if available
    this.initializeAccentColor();
  }

  disconnect() {
    // Clean up event listeners
    this.element.removeEventListener(
      "click",
      this.storeClickCoordinates.bind(this),
    );
  }

  // Initialize the color from CSS variable if available
  initializeAccentColor() {
    try {
      // Get the accent color from CSS variables
      const accentColor = getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim();

      if (accentColor) {
        // If it's an HSL value, convert it to hex
        if (accentColor.startsWith("hsl")) {
          this.accentColor = this.hslToHex(accentColor);
        } else {
          // If it's already a hex or other format, use it directly
          this.accentColor = accentColor.startsWith("#")
            ? accentColor
            : `#${accentColor}`;
        }
      } else {
        this.accentColor = this.defaultColorValue;
      }
    } catch (error) {
      // If there's any error, fall back to the default color
      this.accentColor = this.defaultColorValue;
    }
  }

  // Convert HSL color to hex (copied from effect controller)
  hslToHex(hsl) {
    // Extract h, s, l values from the HSL string
    const hslRegex = /hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/;
    const match = hsl.match(hslRegex);

    if (!match) return this.defaultColorValue;

    let h = parseInt(match[1]) / 360;
    let s = parseInt(match[2]) / 100;
    let l = parseInt(match[3]) / 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    // Convert to hex
    const toHex = (x) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  storeClickCoordinates(event) {
    this.lastClickX = event.clientX;
    this.lastClickY = event.clientY;
  }

  // This action is called when a radio button is clicked
  handleCompletion(event) {
    // Get the clicked input element (which has the task color data attribute)
    const clickedInput = event.currentTarget;

    // Only proceed if this is a completion action
    if (clickedInput.value !== "completed") return;

    // Use stored coordinates instead of event coordinates (which are undefined for hidden inputs)
    const clickX = this.lastClickX;
    const clickY = this.lastClickY;

    // Get task color if available (from data attribute or default)
    // Try both kebab-case and camelCase versions of the attribute
    const taskColor =
      clickedInput.dataset.taskColor ||
      clickedInput.getAttribute("data-task-color") ||
      this.accentColor ||
      this.defaultColorValue;

    // Dispatch the task:completed event
    if (clickX && clickY) {
      document.dispatchEvent(
        new CustomEvent("task:completed", {
          detail: {
            clientX: clickX,
            clientY: clickY,
            color: taskColor,
          },
        }),
      );
    }

    // Important: Don't prevent default or stop propagation
    // This allows the radio-group controller to also handle the event
  }
}
